import React, { useContext, useMemo, useState } from 'react';

import { removeModelLog } from '@libs/NwLogs';

import { ListContext } from '@contexts/ListContext';
import { useStateValue } from '@contexts/StateProvider';

import { useArchiveContact, useUnarchiveContact } from '@hooks/API/Contact/UseContact';
import { useModelsManagementList } from '@hooks/API/Contact/UseContactsList';

import FlexContainer from '@components/Gui/FlexContainer';
import MiniLoading from '@components/Gui/MiniLoading';
import { NwButton } from '@components/Gui/NwButton';
import RadioButtonGroup from '@components/Gui/NwForm/RadioButtonGroup';
import NwIcon from '@components/Gui/NwIcon';

import {
    ActionItem,
    ActionItemBody,
    ActionItemDescription,
    ActionItemFooter,
    ActionItemIcon,
    ActionItemTitle,
} from './ContactDetailsActions-ui';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const storageOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Archived', value: 'archived' },
];

const agencyNamesById = (accountingGroups) => {
    const names = {};
    (accountingGroups || []).forEach((group) => {
        (group.NetworkAgencies || []).forEach((agency) => {
            names[agency.ID] = agency.Name;
        });
    });
    return names;
};

const ManagementModelArchiveAction = ({ contact }) => {
    const { cachedList } = useContext(ListContext);
    const [{ areaContext }] = useStateValue();
    const { data: models, isLoading } = useModelsManagementList();
    const { mutateAsync: onArchiveContact } = useArchiveContact();
    const { mutateAsync: onUnarchiveContact } = useUnarchiveContact();
    const [loadingAgencyId, setLoadingAgencyId] = useState(null);
    const [archivingAll, setArchivingAll] = useState(false);
    const [pendingStorage, setPendingStorage] = useState(null);

    const managementModel = (models || []).find((model) => Number(model.ID) === Number(contact.ID));
    const networkAgencies = managementModel?.NWAGS || [];
    const agencyNames = useMemo(() => agencyNamesById(cachedList?.accountingGroups?.items), [cachedList]);

    const handleChangeStorage = (networkAgency) => async (storageValue) => {
        const storageBooking = storageValue === 'archived';
        setLoadingAgencyId(networkAgency.NetworkAgencyID);
        setPendingStorage({
            networkAgencyId: networkAgency.NetworkAgencyID,
            value: storageValue,
        });
        try {
            const payload = {
                contactId: contact.ID,
                networkAgencyId: networkAgency.NetworkAgencyID,
                contactType: 'Model',
                area: areaContext.area,
            };
            if (storageBooking) {
                await onArchiveContact(payload);
                removeModelLog(contact.ID, areaContext.area);
            } else {
                await onUnarchiveContact(payload);
            }
        } catch (error) {
            console.error('Store Contact Error => ', error);
        }
        setLoadingAgencyId(null);
        setPendingStorage(null);
    };

    const handleArchiveAll = async () => {
        setArchivingAll(true);
        try {
            await onArchiveContact({
                contactId: contact.ID,
                networkAgencyId: null,
                contactType: 'Model',
                area: areaContext.area,
            });
            removeModelLog(contact.ID, areaContext.area);
        } catch (error) {
            console.error('Store Contact Error => ', error);
        }
        setArchivingAll(false);
    };

    const storageValue = (networkAgency) => {
        if (pendingStorage && pendingStorage.networkAgencyId === networkAgency.NetworkAgencyID) {
            return pendingStorage.value;
        }
        return networkAgency.StorageBooking ? 'archived' : 'active';
    };

    return (
        <ActionItem>
            <ActionItemIcon>
                <NwIcon size="3x" icon={byPrefixAndName.fasl['box-archive']} />
            </ActionItemIcon>
            <ActionItemBody>
                <ActionItemTitle>Archive Model</ActionItemTitle>
                {isLoading && !managementModel ? (
                    <ActionItemDescription>
                        <MiniLoading />
                    </ActionItemDescription>
                ) : networkAgencies.length === 0 ? (
                    <ActionItemDescription>This model is not linked to any network agency</ActionItemDescription>
                ) : (
                    networkAgencies.map((networkAgency) => (
                        <FlexContainer
                            key={networkAgency.ID || networkAgency.NetworkAgencyID}
                            justifyContent="space-between"
                            mg="8px 0 0 0"
                        >
                            <div>{agencyNames[networkAgency.NetworkAgencyID] || networkAgency.NetworkAgencyID}</div>
                            <RadioButtonGroup
                                value={storageValue(networkAgency)}
                                onChange={handleChangeStorage(networkAgency)}
                                size="small"
                                disabled={archivingAll || loadingAgencyId === networkAgency.NetworkAgencyID}
                                options={storageOptions}
                            />
                        </FlexContainer>
                    ))
                )}
                {networkAgencies.length > 1 && (
                    <ActionItemFooter>
                        <NwButton
                            danger
                            loading={archivingAll}
                            disabled={loadingAgencyId != null}
                            label="archive for all agencies"
                            onClick={handleArchiveAll}
                        />
                    </ActionItemFooter>
                )}
            </ActionItemBody>
        </ActionItem>
    );
};

export default ManagementModelArchiveAction;
